(function () {
  "use strict";

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const lerp = (a, b, t) => a + (b - a) * t;
  const easeInOut = (t) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

  function initRoot(root) {
    if (!root || root.dataset.htInit === "1") return;
    root.dataset.htInit = "1";
    bindRails(root);
    bindShopRows(root);
    bindGridRails(root);
    bindRailClickThrough(root);
    bindGallery(root);
    bindEnhancedMotion(root);
    bindReveal(root);
    bindTimeline(root);
    bindGuide(root);
    bindFab(root);
    bindDrift(root);
    bindArrival(root);
    bindDaybreak(root);
    buildTank(root);
    bindFlare(root);
    bindDecor(root);
  }

  function initAll(scope) {
    if (scope && scope.matches && scope.matches("[data-ht-aqua]")) { initRoot(scope); return; }
    (scope || document).querySelectorAll("[data-ht-aqua]").forEach(initRoot);
  }

  // Register editor listener once, even if Shopify re-executes this script.
  if (!window.__htLandingBooted) {
    window.__htLandingBooted = true;
    document.addEventListener("shopify:section:load", function (event) { initAll(event.target); });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { initAll(); });
  } else {
    initAll();
  }

  function bindRails(root) {
    const catItems = root.querySelector("#catItems");
    const catNext = root.querySelector("#catNext");
    if (catNext && catItems) catNext.addEventListener("click", () => catItems.scrollBy({ left: 240, behavior: "smooth" }));

    const productGrid = root.querySelector("#productGrid");
    const railNext = root.querySelector("#railNext");
    if (railNext && productGrid) {
      railNext.addEventListener("click", () => {
        productGrid.style.overflowX = "auto";
        productGrid.scrollBy({ left: 260, behavior: "smooth" });
      });
    }
  }

  function scrollRailTrack(track, delta) {
    if (!track) return;
    const max = Math.max(0, track.scrollWidth - track.clientWidth);
    const target = clamp(track.scrollLeft + delta, 0, max);
    const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    track.scrollTo({ left: target, behavior: reduceMotion ? "auto" : "smooth" });

    // Some browsers/theme wrappers can swallow a relative smooth scroll on snapped rails.
    // Force the exact target if the track did not move after the click.
    window.setTimeout(() => {
      if (Math.abs(track.scrollLeft - target) > 2 && target >= 0 && target <= max) {
        track.scrollLeft = target;
      }
    }, reduceMotion ? 0 : 140);
  }

  function isPointerInside(event, element) {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
  }

  function bindRailHitArea(rail, track, prev, next, stepBy) {
    rail.addEventListener("click", (event) => {
      const direction = isPointerInside(event, prev) ? -1 : (isPointerInside(event, next) ? 1 : 0);
      if (!direction) return;
      event.preventDefault();
      event.stopPropagation();
      scrollRailTrack(track, stepBy() * direction);
    }, true);
  }

  function getRailTrackFromButton(button) {
    const shopRail = button.closest(".shop-row-rail");
    if (shopRail) return shopRail.querySelector(".shop-row-track");
    const gridRail = button.closest(".ht-rail");
    if (gridRail) return gridRail.querySelector(".ht-rail-track");
    return null;
  }

  function getRailStep(track) {
    const card = track ? track.querySelector(".shop-card") || track.children[0] : null;
    const gap = track && track.classList.contains("shop-row-track") ? 16 : 18;
    const w = card ? card.getBoundingClientRect().width + gap : 280;
    return Math.max(w, w * Math.floor(track.clientWidth / w));
  }

  function getRailControlAt(root, event) {
    const controls = root.querySelectorAll(".shop-row-prev, .shop-row-next, .ht-rail-prev, .ht-rail-next");
    for (const control of controls) {
      if (isPointerInside(event, control)) return control;
    }
    return null;
  }

  function bindRailClickThrough(root) {
    root.addEventListener("pointermove", (event) => {
      if (getRailControlAt(root, event)) root.dispatchEvent(new CustomEvent("ht:close-fab"));
    }, true);

    root.addEventListener("click", (event) => {
      const control = getRailControlAt(root, event);
      if (!control) return;
      const track = getRailTrackFromButton(control);
      if (!track) return;
      const direction = control.classList.contains("shop-row-prev") || control.classList.contains("ht-rail-prev") ? -1 : 1;
      event.preventDefault();
      event.stopPropagation();
      root.dispatchEvent(new CustomEvent("ht:close-fab"));
      scrollRailTrack(track, getRailStep(track) * direction);
    }, true);
  }

  function bindShopRows(root) {
    root.querySelectorAll(".shop-row-rail").forEach((rail) => {
      const track = rail.querySelector(".shop-row-track");
      const prev = rail.querySelector(".shop-row-prev");
      const next = rail.querySelector(".shop-row-next");
      if (!track) return;
      const stepBy = () => {
        const card = track.querySelector(".shop-card");
        const w = card ? card.getBoundingClientRect().width + 16 : 280;
        return Math.max(w, w * Math.floor(track.clientWidth / w)); // jump ~one screenful, min one card
      };
      if (next) next.addEventListener("click", (event) => {
        event.preventDefault();
        scrollRailTrack(track, stepBy());
      });
      if (prev) prev.addEventListener("click", (event) => {
        event.preventDefault();
        scrollRailTrack(track, -stepBy());
      });
      bindRailHitArea(rail, track, prev, next, stepBy);
      const sync = () => {
        const max = track.scrollWidth - track.clientWidth - 4;
        const overflow = max > 4;
        // Never hide the arrows (so they show even if overflow is mis-measured); just dim at the ends.
        if (prev) prev.style.opacity = (!overflow || track.scrollLeft <= 4) ? "0.4" : "1";
        if (next) next.style.opacity = (!overflow || track.scrollLeft >= max) ? "0.4" : "1";
      };
      sync();
      track.addEventListener("scroll", sync, { passive: true });
      window.addEventListener("resize", sync);
    });
  }

  // Turn compact repeated grids into
  // smooth horizontal sliders: wrap each grid, inject prev/next arrows, wire scroll.
  function bindGridRails(root) {
    const tracks = root.querySelectorAll(".evening-grid, .reviews-grid, .curated .product-grid");
    tracks.forEach((track) => {
      if (track.dataset.railed === "1" || track.children.length < 2) return;
      track.dataset.railed = "1";
      track.classList.add("ht-rail-track");

      const rail = document.createElement("div");
      rail.className = "ht-rail";
      track.parentNode.insertBefore(rail, track);
      rail.appendChild(track);

      const prev = document.createElement("button");
      const next = document.createElement("button");
      prev.type = "button"; next.type = "button";
      prev.className = "ht-rail-btn ht-rail-prev";
      next.className = "ht-rail-btn ht-rail-next";
      prev.setAttribute("aria-label", "Previous");
      next.setAttribute("aria-label", "Next");
      prev.innerHTML = '<svg viewBox="0 0 24 24" width="16" fill="none" stroke="currentColor" stroke-width="1.5"><path d="m15 6-6 6 6 6"/></svg>';
      next.innerHTML = '<svg viewBox="0 0 24 24" width="16" fill="none" stroke="currentColor" stroke-width="1.5"><path d="m9 6 6 6-6 6"/></svg>';
      rail.append(prev, next);

      const stepBy = () => {
        const card = track.children[0];
        const w = card ? card.getBoundingClientRect().width + 18 : 300;
        return Math.max(w, w * Math.floor(track.clientWidth / w));
      };
      next.addEventListener("click", (event) => {
        event.preventDefault();
        scrollRailTrack(track, stepBy());
      });
      prev.addEventListener("click", (event) => {
        event.preventDefault();
        scrollRailTrack(track, -stepBy());
      });
      bindRailHitArea(rail, track, prev, next, stepBy);

      const sync = () => {
        const max = track.scrollWidth - track.clientWidth - 4;
        const overflow = max > 4;
        // Never hide the arrows; just dim at the ends.
        prev.style.opacity = (!overflow || track.scrollLeft <= 4) ? "0.4" : "1";
        next.style.opacity = (!overflow || track.scrollLeft >= max) ? "0.4" : "1";
      };
      sync();
      track.addEventListener("scroll", sync, { passive: true });
      window.addEventListener("resize", sync);
    });
  }

  function bindGallery(root) {
    const track = root.querySelector("#galTrack");
    const prev = root.querySelector("#galPrev");
    const next = root.querySelector("#galNext");
    if (!track || !prev || !next) return;

    const cards = Array.from(track.querySelectorAll(".gallery-card"));
    if (!cards.length) return;
    let center = Math.floor(cards.length / 2);

    function render() {
      const step = window.matchMedia("(max-width: 1080px)").matches ? 150 : 240;
      cards.forEach((card, index) => {
        const offset = index - center;
        const abs = Math.abs(offset);
        const sign = offset > 0 ? 1 : (offset < 0 ? -1 : 0);
        const tx = offset * step;
        const rot = -sign * 42;
        const scale = offset === 0 ? 1 : Math.max(0.72, 0.86 - (abs - 1) * 0.08);
        const tz = -abs * 140;
        card.classList.toggle("center", offset === 0);
        card.style.zIndex = String(20 - abs);
        card.style.opacity = abs > 2 ? "0" : (offset === 0 ? "1" : "0.65");
        card.style.pointerEvents = abs > 2 ? "none" : "auto";
        card.style.transform = "translate(-50%, -50%) translateX(" + tx + "px) translateZ(" + tz + "px) rotateY(" + rot + "deg) scale(" + scale.toFixed(3) + ")";
      });
    }

    cards.forEach((card, index) => card.addEventListener("click", () => {
      center = index;
      render();
    }));
    prev.addEventListener("click", () => {
      center = (center - 1 + cards.length) % cards.length;
      render();
    });
    next.addEventListener("click", () => {
      center = (center + 1) % cards.length;
      render();
    });
    render();
  }

  function bindReviews(root) {
    const grid = root.querySelector("#reviewsGrid");
    const dots = root.querySelector("#reviewDots");
    if (!grid || !dots) return;
    const cards = Array.from(grid.querySelectorAll(".review-card"));
    const perPage = window.matchMedia("(max-width: 700px)").matches ? 1 : 4;
    const pages = Math.max(1, Math.ceil(cards.length / perPage));
    let page = 0;

    if (pages <= 1) return;

    function render() {
      cards.forEach((card, index) => {
        const visible = index >= page * perPage && index < page * perPage + perPage;
        card.style.display = visible ? "" : "none";
      });
      Array.from(dots.children).forEach((dot, index) => dot.classList.toggle("active", index === page));
    }

    for (let index = 0; index < pages; index += 1) {
      const button = document.createElement("button");
      button.type = "button";
      button.addEventListener("click", () => {
        page = index;
        render();
      });
      dots.appendChild(button);
    }
    render();
    setInterval(() => {
      page = (page + 1) % pages;
      render();
    }, 6000);
  }

  function bindEnhancedMotion(root) {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

    if (fine && !document.querySelector(".cursor-ring")) {
      document.documentElement.classList.add("has-cursor");
      const ring = document.createElement("div");
      const dot = document.createElement("div");
      ring.className = "cursor-ring";
      dot.className = "cursor-dot";
      document.body.append(ring, dot);
      let mx = innerWidth / 2;
      let my = innerHeight / 2;
      let rx = mx;
      let ry = my;
      window.addEventListener("mousemove", (event) => {
        mx = event.clientX;
        my = event.clientY;
        dot.style.transform = "translate(" + mx + "px, " + my + "px) translate(-50%,-50%)";
        ring.style.opacity = "1";
        dot.style.opacity = "1";
      });
      (function cursorFrame() {
        rx = lerp(rx, mx, 0.18);
        ry = lerp(ry, my, 0.18);
        ring.style.transform = "translate(" + rx + "px, " + ry + "px) translate(-50%,-50%)";
        requestAnimationFrame(cursorFrame);
      })();
      window.addEventListener("mousedown", () => ring.classList.add("tap"));
      window.addEventListener("mouseup", () => ring.classList.remove("tap"));
      root.querySelectorAll("a,button,.product-card,.coll-card,.eve-card,.gallery-card,.cat-item,.review-card,.wish,.mq-card,.creature,.drift-obj").forEach((el) => {
        el.addEventListener("mouseenter", () => ring.classList.add("hot"));
        el.addEventListener("mouseleave", () => ring.classList.remove("hot"));
      });
    }

    root.querySelectorAll(".btn,.play-circle,.cat-arrow,.gallery-arrow,.rail-next,.eve-arrow,.icon-btn").forEach((el) => {
      el.classList.add("magnetic");
      el.addEventListener("mousemove", (event) => {
        if (reduce) return;
        const rect = el.getBoundingClientRect();
        const x = (event.clientX - rect.left - rect.width / 2) * 0.4;
        const y = (event.clientY - rect.top - rect.height / 2) * 0.4;
        el.style.transform = "translate(" + x + "px, " + y + "px)";
      });
      el.addEventListener("mouseleave", () => {
        el.style.transform = "";
      });
    });

    root.querySelectorAll(".product-card").forEach((card) => {
      const media = card.querySelector(".product-media");
      if (!media) return;
      // Cards inside a horizontal slider sit in an overflow:auto track, which clips
      // a big 3D lift. Use a gentle lift there so the hover stays fully visible.
      const inRail = !!card.closest(".shop-row-track, .ht-rail-track");
      card.addEventListener("pointerenter", () => {
        card.classList.add("lift");
        media.style.transition = "transform .18s ease-out, box-shadow .5s ease, border-radius .5s ease";
      });
      card.addEventListener("pointermove", (event) => {
        const rect = card.getBoundingClientRect();
        const px = (event.clientX - rect.left) / rect.width - 0.5;
        const py = (event.clientY - rect.top) / rect.height - 0.5;
        if (inRail) {
          media.style.transform = "translateY(-10px) scale(1.05) rotateY(" + (px * 10) + "deg) rotateX(" + (-py * 10) + "deg)";
        } else {
          media.style.transform = "translateY(-30px) translateZ(90px) scale(1.14) rotateY(" + (px * 18) + "deg) rotateX(" + (-py * 18) + "deg)";
        }
      });
      card.addEventListener("pointerleave", () => {
        card.classList.remove("lift");
        media.style.transition = "transform .6s cubic-bezier(.2,.7,.3,1), box-shadow .6s ease, border-radius .6s ease";
        media.style.transform = "";
      });
    });

    root.querySelectorAll(".coll-card,.eve-card").forEach((card) => {
      card.addEventListener("pointermove", (event) => {
        const rect = card.getBoundingClientRect();
        const px = (event.clientX - rect.left) / rect.width - 0.5;
        const py = (event.clientY - rect.top) / rect.height - 0.5;
        card.classList.add("tilt");
        card.style.transform = "rotateY(" + (px * 9) + "deg) rotateX(" + (-py * 9) + "deg) translateZ(20px)";
      });
      card.addEventListener("pointerleave", () => {
        card.classList.remove("tilt");
        card.style.transform = "";
      });
    });

    if (!reduce) {
      const orbConfig = [
        { sel: ".hero", top: "22%", left: "78%", size: 120, depth: 0.22, kind: "cool", dur: 11, fg: false, blur: 0 },
        { sel: ".hero", top: "64%", left: "60%", size: 54, depth: 0.5, kind: "", dur: 8, fg: true, blur: 2 },
        { sel: ".featured", top: "12%", left: "46%", size: 70, depth: 0.35, kind: "cool", dur: 9, fg: false, blur: 0 },
        { sel: ".curated", top: "78%", left: "8%", size: 90, depth: 0.18, kind: "", dur: 12, fg: false, blur: 0 },
        { sel: ".immerse", top: "20%", left: "88%", size: 64, depth: 0.4, kind: "cool", dur: 10, fg: true, blur: 1 },
        { sel: ".orbit", top: "8%", left: "70%", size: 100, depth: 0.25, kind: "cool", dur: 13, fg: false, blur: 0 },
        { sel: "#drift", top: "16%", left: "86%", size: 64, depth: 0.38, kind: "cool", dur: 10, fg: true, blur: 1 },
        { sel: ".daybreak", top: "30%", left: "10%", size: 84, depth: 0.3, kind: "warm", dur: 12, fg: false, blur: 0 },
        { sel: ".guide", top: "78%", left: "6%", size: 64, depth: 0.4, kind: "warm", dur: 11, fg: true, blur: 1 },
        { sel: ".tankzone", top: "14%", left: "8%", size: 66, depth: 0.34, kind: "cool", dur: 12, fg: false, blur: 0 }
      ];
      const floaters = [];
      orbConfig.forEach((config) => {
        const host = root.querySelector(config.sel);
        if (!host) return;
        const floater = document.createElement("div");
        floater.className = "floater" + (config.fg ? " fg" : "");
        floater.style.cssText = "top:" + config.top + ";left:" + config.left + ";width:" + config.size + "px;height:" + config.size + "px;" + (config.fg ? "opacity:" + (0.5 - config.blur * 0.08) + ";filter:blur(" + config.blur + "px);" : "opacity:0.95;");
        const inner = document.createElement("div");
        inner.className = "floater-inner orb " + config.kind;
        inner.style.setProperty("--dur", config.dur + "s");
        inner.style.animationDelay = (-Math.random() * config.dur) + "s";
        floater.appendChild(inner);
        host.appendChild(floater);
        const rect = floater.getBoundingClientRect();
        floaters.push({ el: floater, depth: config.depth, pageY: rect.top + window.scrollY + config.size / 2 });
      });

      const pure = root.querySelector("#pure");
      if (pure) {
        const field = document.createElement("div");
        field.className = "bubble-field";
        for (let i = 0; i < 10; i += 1) {
          const bubble = document.createElement("span");
          const size = 4 + Math.random() * 16;
          bubble.className = "bubble";
          bubble.style.cssText = "left:" + (Math.random() * 100) + "%;width:" + size + "px;height:" + size + "px;animation-duration:" + (7 + Math.random() * 9) + "s;animation-delay:" + (-Math.random() * 12) + "s;--drift:" + (Math.random() * 80 - 40) + "px;";
          field.appendChild(bubble);
        }
        pure.insertBefore(field, pure.firstChild);
      }

      const progress = document.createElement("div");
      progress.className = "scroll-progress";
      root.appendChild(progress);
      const heroPh = root.querySelector(".hero-bg .ph");
      const targetCard = root.querySelector(".curated #productGrid .product-card");
      const targetMedia = targetCard ? targetCard.querySelector(".product-media") : null;
      // Use the first available winter product image as the flying drop; fall back to a glass orb.
      const firstProductImg = targetCard ? targetCard.querySelector("img") : null;
      // Prefer the section image; otherwise use the selected target product image.
      const flyerSrc = root.dataset.htFlyer || (firstProductImg ? (firstProductImg.currentSrc || firstProductImg.getAttribute("src") || "") : "");
      const flyerLabel = (root.dataset.htFlyerLabel || "").trim();
      const flyerSize = parseInt(root.dataset.htFlyerSize, 10) || 260;
      const flyer = document.createElement("div");
      flyer.id = "flyer";
      flyer.style.width = flyerSize + "px";
      flyer.style.height = flyerSize + "px";
      flyer.innerHTML = flyerSrc
        ? '<img class="flyer-img" src="' + flyerSrc + '" alt="">'
        : '<div class="floater-inner orb cool" style="animation:bob 6s ease-in-out infinite"></div>';
      if (flyerLabel) {
        const lab = document.createElement("span");
        lab.className = "label";
        lab.textContent = flyerLabel;
        flyer.appendChild(lab);
      }
      root.appendChild(flyer);
      let caught = false;

      function frame() {
        const sy = window.scrollY;
        const vh = innerHeight;
        const max = document.documentElement.scrollHeight - vh;
        progress.style.width = (clamp(sy / (max || 1), 0, 1) * 100) + "%";
        if (heroPh && sy < vh * 1.3) {
          heroPh.style.transform = "translateY(" + (sy * 0.18) + "px) scale(" + (1 + sy / vh * 0.08) + ")";
        }
        floaters.forEach((floater) => {
          const ty = ((sy + vh / 2) - floater.pageY) * floater.depth;
          floater.el.style.transform = "translate3d(0, " + ty.toFixed(1) + "px, 0)";
        });
        if (targetMedia) {
          const rect = targetMedia.getBoundingClientRect();
          const cardCenterY = rect.top + rect.height / 2;
          const cardCenterX = rect.left + rect.width / 2;
          const scrollAtCenter = cardCenterY + sy - vh / 2;
          const p = clamp(sy / (scrollAtCenter || 1), 0, 1);
          if (p < 0.995) {
            const eased = easeInOut(p);
            // start: large, parked on the right of the hero; end: shrinks into the first product card
            const x = lerp(innerWidth * 0.72, cardCenterX, eased);
            const y = lerp(vh * 0.4, cardCenterY, eased);
            const scale = lerp(1, (rect.width * 0.7) / flyerSize, eased);
            const opacity = p > 0.9 ? (0.995 - p) / 0.095 : 1;
            flyer.style.opacity = opacity.toFixed(2);
            flyer.style.transform = "translate(" + x + "px, " + y + "px) translate(-50%,-50%) scale(" + scale.toFixed(3) + ")";
            caught = false;
          } else {
            flyer.style.opacity = "0";
            if (!caught && targetCard) {
              caught = true;
              targetCard.classList.add("caught");
              setTimeout(() => targetCard.classList.remove("caught"), 950);
            }
          }
        }
        requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }

    root.querySelectorAll(".hero h1,.featured-head h2,.curated-intro h2,.shopneed-head h2,.immerse-head h2,.create-copy h2,.orbit-copy h2,.pure-copy h2,.reviews-head h2,.community-head h2,.news-panel h2,.story-head h2,.guide-head h2,.faq-head h2,.journal-head h2,.tankzone-head h2").forEach((heading) => {
      heading.classList.add("reveal-h");
    });
    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("in");
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.15 });
      root.querySelectorAll(".reveal-h").forEach((heading) => observer.observe(heading));
    }
    setTimeout(() => root.querySelectorAll(".reveal-h").forEach((heading) => heading.classList.add("in")), 1800);
  }

  function bindReveal(root) {
    const selector = ".featured,.curated,.shopneed,.arrival,.immerse,.story,.create,.evening,.orbit,.pure,.playground,.daybreak,.spotlight,.reviews,.daylight,.tankzone,.guide,.faq,.stats,.community,.journal,.exclusive,.newsletter,.footer-features";
    const targets = root.querySelectorAll(selector);
    if (!("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.style.opacity = "1";
        entry.target.style.transform = "none";
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.08 });

    targets.forEach((target) => {
      // Animate the inner content, not the whole <section>, so the section's own
      // background keeps painting — otherwise a transparent (opacity:0) light-theme
      // section reveals the dark page background behind it as a black gap.
      const inner = target.querySelector(":scope > .wrap") || target;
      inner.style.opacity = "0";
      inner.style.transform = "translateY(28px)";
      inner.style.transition = "opacity .9s ease, transform .9s cubic-bezier(.2,.7,.3,1)";
      observer.observe(inner);
    });
  }

  function bindTimeline(root) {
    const timeline = root.querySelector("#timeline");
    const fill = root.querySelector("#tlFill");
    if (!timeline || !fill) return;
    const items = Array.from(timeline.querySelectorAll(".tl-item"));

    function update() {
      const rect = timeline.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, (window.innerHeight * 0.72 - rect.top) / Math.max(1, rect.height)));
      fill.style.height = (progress * 100).toFixed(2) + "%";
      items.forEach((item) => {
        const itemRect = item.getBoundingClientRect();
        const lit = itemRect.top < window.innerHeight * 0.72;
        item.classList.toggle("in", lit);
        item.classList.toggle("lit", lit);
      });
    }

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
  }

  function bindGuide(root) {
    const steps = Array.from(root.querySelectorAll("#guideSteps .guide-step"));
    const visuals = Array.from(root.querySelectorAll("#guideVisual .ph"));
    const progress = root.querySelector("#guideProgress");
    const num = root.querySelector("#guideNum");
    if (!steps.length || !visuals.length) return;

    function setActive(index) {
      steps.forEach((step, i) => step.classList.toggle("active", i === index));
      visuals.forEach((visual, i) => visual.classList.toggle("active", i === index));
      if (progress) progress.style.width = (((index + 1) / steps.length) * 100).toFixed(2) + "%";
      if (num) num.textContent = String(index + 1).padStart(2, "0");
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const index = steps.indexOf(entry.target);
        if (index >= 0) setActive(index);
      });
    }, { threshold: 0.55 });

    steps.forEach((step) => observer.observe(step));
    setActive(0);

    // Manual "sticky" for the visual — robust even if a theme ancestor has overflow:hidden
    // (which silently disables CSS position:sticky). Desktop only; mobile stacks normally.
    const grid = root.querySelector(".guide-grid");
    const sticky = root.querySelector(".guide-sticky");
    if (grid && sticky) {
      const sync = () => {
        if (!window.matchMedia("(min-width: 1081px)").matches) {
          sticky.style.position = "";
          sticky.style.top = "";
          sticky.style.transform = "";
          return;
        }
        sticky.style.position = "relative";
        sticky.style.top = "0";
        sticky.style.willChange = "transform";
        const pin = window.innerHeight * 0.14;
        const gr = grid.getBoundingClientRect();
        const range = Math.max(0, grid.offsetHeight - sticky.offsetHeight);
        let ty = pin - gr.top;
        ty = Math.max(0, Math.min(ty, range));
        sticky.style.transform = "translateY(" + ty.toFixed(1) + "px)";
      };
      sync();
      window.addEventListener("scroll", sync, { passive: true });
      window.addEventListener("resize", sync);
    }
  }

  function bindFab(root) {
    const nav = root.querySelector("#fabNav");
    const button = root.querySelector("#fabBtn");
    const scrim = root.querySelector("#fabScrim");
    const hint = root.querySelector("#fabHint");
    const ring = button ? button.querySelector(".fab-ring circle") : null;
    if (!nav || !button) return;

    if (ring) {
      const radius = ring.r.baseVal.value;
      const circumference = 2 * Math.PI * radius;
      ring.style.setProperty("--circ", circumference.toFixed(1));
      button.style.setProperty("--circ", circumference.toFixed(1));
    }

    let opened = false;

    function setOpen(open) {
      opened = open;
      nav.classList.toggle("open", open);
      button.setAttribute("aria-expanded", open ? "true" : "false");
      button.setAttribute("aria-label", open ? "Close navigation menu" : "Open navigation menu");
      if (hint && open) hint.classList.remove("show");
    }

    button.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      setOpen(!opened);
    });
    if (scrim) scrim.addEventListener("click", () => setOpen(false));
    root.addEventListener("ht:close-fab", () => setOpen(false));
    document.addEventListener("click", (event) => {
      if (opened && !nav.contains(event.target)) setOpen(false);
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && opened) setOpen(false);
    });
    root.querySelectorAll(".fab-item").forEach((item) => {
      item.addEventListener("click", (event) => {
        const id = item.getAttribute("href");
        const target = id ? root.querySelector(id) || document.querySelector(id) : null;
        if (target) {
          event.preventDefault();
          setOpen(false);
          target.scrollIntoView({ behavior: "smooth" });
        }
      });
    });
    if (hint) {
      setTimeout(() => {
        if (!opened) hint.classList.add("show");
        setTimeout(() => hint.classList.remove("show"), 3400);
      }, 1400);
      button.addEventListener("mouseenter", () => {
        if (!opened) hint.classList.add("show");
      });
      button.addEventListener("mouseleave", () => {
        if (!opened) hint.classList.remove("show");
      });
    }
  }

  function bindDrift(root) {
    const field = root.querySelector("#driftField");
    if (!field) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const shapes = {
      snowflake: '<svg class="drift-svg" viewBox="0 0 100 100" fill="none" stroke-width="3" stroke-linecap="round"><g class="stroke-frost" stroke="#cfe7fb"><path d="M50 8V92M50 8l-9 9M50 8l9 9M50 92l-9-9M50 92l9-9"/><path d="M14 29l72 42M86 29L14 71"/></g></svg>',
      crystal: '<svg class="drift-svg" viewBox="0 0 100 120"><path d="M50 4 84 44 50 116 16 44z" class="fill-frost" opacity="0.85"/><path d="M50 4 84 44 50 60 16 44z" fill="#eef8ff" opacity="0.9"/><path d="M50 4 50 116M16 44 84 44" stroke="#fff" stroke-width="1.5" opacity="0.6"/></svg>',
      plant: '<svg class="drift-svg" viewBox="0 0 100 120"><path d="M50 116c0-30-2-54 0-78" stroke="#3f9e74" stroke-width="3" fill="none"/><path d="M50 70c-14-4-24-18-22-36 16 2 26 16 22 36Z" class="fill-plant"/><path d="M50 56c14-6 22-22 18-40-15 4-23 20-18 40Z" fill="#8fe0b8"/></svg>',
      leaf: '<svg class="drift-svg" viewBox="0 0 100 100"><path d="M86 14C40 18 18 44 16 86c42-2 68-26 70-72Z" class="fill-plant"/><path d="M16 86C40 62 60 40 86 14" stroke="#2f7d57" stroke-width="2.5" fill="none"/></svg>'
    };
    const config = [
      { x: 12, y: 22, size: 92, type: "glass", dur: 9, name: "Frost Orb Shrimp", price: "$64.99" },
      { x: 30, y: 60, size: 70, type: "snowflake", dur: 11, name: "Winter Crystal Set", price: "$29.99" },
      { x: 48, y: 30, size: 110, type: "glass", dur: 13, name: "Mr. Frost Ornament", price: "$18.99" },
      { x: 66, y: 64, size: 80, type: "crystal", dur: 10, name: "Glacier Quartz", price: "$22.99" },
      { x: 82, y: 24, size: 74, type: "plant", dur: 12, name: "Snow Moss", price: "$11.99" },
      { x: 22, y: 80, size: 58, type: "leaf", dur: 8, name: "Frosted Buce Leaf", price: "$16.99" }
    ];
    const objects = [];

    config.forEach((item) => {
      const el = document.createElement("div");
      el.className = "drift-obj";
      el.style.cssText = "left:" + item.x + "%;top:" + item.y + "%;width:" + item.size + "px;height:" + item.size + "px;margin-left:" + (-item.size / 2) + "px;margin-top:" + (-item.size / 2) + "px;";
      const inner = document.createElement("div");
      inner.className = "drift-inner" + (item.type === "glass" ? " glass" : "");
      inner.style.setProperty("--dur", item.dur + "s");
      inner.style.animationDelay = (-Math.random() * item.dur) + "s";
      if (item.type !== "glass") inner.innerHTML = shapes[item.type] || "";
      const tag = document.createElement("span");
      tag.className = "tag-mini";
      tag.textContent = item.name + " - " + item.price;
      el.append(inner, tag);
      field.appendChild(el);
      objects.push({ el, cx: 0, cy: 0, ox: 0, oy: 0 });
    });

    function measure() {
      const fr = field.getBoundingClientRect();
      objects.forEach((obj) => {
        const rect = obj.el.getBoundingClientRect();
        obj.cx = rect.left - fr.left + rect.width / 2;
        obj.cy = rect.top - fr.top + rect.height / 2;
      });
    }

    measure();
    window.addEventListener("resize", measure);
    if (reduce) return;

    let mx = -9999;
    let my = -9999;
    let active = false;
    let raf = null;
    const radius = 170;

    function physics() {
      let moving = false;
      objects.forEach((obj) => {
        let tx = 0;
        let ty = 0;
        const dx = obj.cx + obj.ox - mx;
        const dy = obj.cy + obj.oy - my;
        const dist = Math.hypot(dx, dy);
        if (active && dist < radius && dist > 0.01) {
          const force = Math.pow((radius - dist) / radius, 2) * radius * 0.9;
          tx = dx / dist * force;
          ty = dy / dist * force;
        }
        obj.ox = lerp(obj.ox, tx, 0.18);
        obj.oy = lerp(obj.oy, ty, 0.18);
        if (Math.abs(obj.ox - tx) > 0.3 || Math.abs(obj.oy - ty) > 0.3 || Math.abs(obj.ox) > 0.3 || Math.abs(obj.oy) > 0.3) moving = true;
        obj.el.style.transform = "translate3d(" + obj.ox.toFixed(1) + "px, " + obj.oy.toFixed(1) + "px, 0)";
      });
      if (moving || active) raf = requestAnimationFrame(physics);
      else raf = null;
    }

    function kick() {
      if (!raf) raf = requestAnimationFrame(physics);
    }

    field.addEventListener("pointermove", (event) => {
      const rect = field.getBoundingClientRect();
      mx = event.clientX - rect.left;
      my = event.clientY - rect.top;
      active = true;
      kick();
    });
    field.addEventListener("pointerleave", () => {
      active = false;
      mx = -9999;
      my = -9999;
      kick();
    });
  }

  function bindArrival(root) {
    const stage = root.querySelector("#arrivalStage");
    const flyer = root.querySelector("#arrivalFlyer");
    const target = root.querySelector("#arrivalTarget");
    if (!stage || !flyer || !target || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let lit = false;
    function frame() {
      const sr = stage.getBoundingClientRect();
      const p = clamp((innerHeight * 0.85 - sr.top) / (innerHeight * 0.62), 0, 1);
      const tr = target.getBoundingClientRect();
      const startX = sr.width * 0.86;
      const startY = -sr.height * 0.06;
      const endX = tr.left - sr.left + tr.width / 2;
      const endY = tr.top - sr.top + tr.height * 0.46;
      const eased = easeInOut(p);
      const x = lerp(startX, endX, eased);
      const y = lerp(startY, endY, eased);
      const scale = lerp(1, 0.34, eased);
      const opacity = p > 0.86 ? clamp((1 - p) / 0.14, 0, 1) : 1;
      flyer.style.transform = "translate(" + x + "px, " + y + "px) translate(-50%,-50%) scale(" + scale.toFixed(3) + ")";
      flyer.style.opacity = opacity.toFixed(2);
      if (p > 0.9 && !lit) {
        lit = true;
        target.classList.add("lit");
        setTimeout(() => target.classList.remove("lit"), 1100);
      }
      if (p < 0.85) lit = false;
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  function bindDaybreak(root) {
    const daybreak = root.querySelector("#daybreak");
    if (!daybreak || !("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) daybreak.classList.add("revealed");
      });
    }, { threshold: 0.3 });
    observer.observe(daybreak);
  }

  function buildTank(root) {
    const tank = root.querySelector("#tank");
    if (!tank) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const creatures = [
      { name: "Winter Angelfish", w: 118, y: 24, speed: 30, color: "#bfe3ff" },
      { name: "Neon Tetra", w: 72, y: 42, speed: 48, color: "#37c7ff" },
      { name: "Crystal Shrimp", w: 78, y: 72, speed: 14, color: "#ff7a8a" },
      { name: "Frost Nerite", w: 68, y: 80, speed: 8, color: "#e8a15c" }
    ];

    creatures.forEach((creature, index) => {
      const el = document.createElement("div");
      el.className = "creature";
      el.style.width = creature.w + "px";
      el.style.height = Math.round(creature.w * 0.58) + "px";
      el.dataset.speed = creature.speed;
      el.dataset.phase = index * 1.7;
      el.dataset.y = creature.y;
      el.dataset.x = String(8 + index * 18);
      el.innerHTML = '<span class="creature-tag">' + creature.name + '</span><div class="creature-body"><svg viewBox="0 0 140 80" aria-hidden="true"><path d="M34 40 L6 18 Q16 40 6 62 Z" fill="' + creature.color + '" opacity=".75"/><ellipse cx="78" cy="40" rx="42" ry="22" fill="' + creature.color + '"/><path d="M96 24 L126 10 Q116 40 126 70 L96 56" fill="' + creature.color + '" opacity=".65"/><circle cx="104" cy="34" r="4" fill="#06101d"/><circle cx="105" cy="32.5" r="1.4" fill="#fff"/></svg></div>';
      tank.appendChild(el);
    });

    if (reduce) return;
    let last = performance.now();
    function loop(now) {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      const width = tank.clientWidth;
      const height = tank.clientHeight;
      root.querySelectorAll("#tank .creature").forEach((el) => {
        let x = Number(el.dataset.x);
        let dir = Number(el.dataset.dir || 1);
        const speed = Number(el.dataset.speed);
        x += dir * speed * dt / Math.max(1, width) * 100;
        if (x > 92) { x = 92; dir = -1; }
        if (x < 2) { x = 2; dir = 1; }
        el.dataset.x = String(x);
        el.dataset.dir = String(dir);
        const phase = Number(el.dataset.phase);
        const y = Number(el.dataset.y) + Math.sin(now / 900 + phase) * 2.2;
        el.style.transform = "translate3d(" + (x / 100 * width).toFixed(1) + "px," + (y / 100 * height).toFixed(1) + "px,0)";
        const body = el.querySelector(".creature-body");
        if (body) body.style.transform = "scaleX(" + dir + ")";
      });
      requestAnimationFrame(loop);
    }
    requestAnimationFrame(loop);
  }

  function bindFlare(root) {
    const flare = root.querySelector("#flare");
    // Drive the dedicated "light gate" spacer section that sits between the last
    // night section and Daybreak. Because the gate owns its own scroll space and
    // has a dark backdrop, only the gate is in the viewport during the middle of
    // the transition — the previous/next sections are fully scrolled out, so the
    // next section can never be seen awkwardly sliding through the transition.
    const gate = root.querySelector("#lightgate");
    const daybreak = root.querySelector("#daybreak");
    if (!flare || !gate || !daybreak) return;

    // A dawn wash that grows over the light gate without becoming a blank page.
    const core = document.createElement("div");
    core.id = "lightcore";
    root.appendChild(core);

    const MAX_SCALE = 3.4; // big enough for the solid centre to cover the corners
    const MAX_OPACITY = 0.78;

    function update() {
      const vh = window.innerHeight;
      // Drive everything off Daybreak's own position. The bloom peaks at the
      // hand-off, then clears while Daybreak enters so the morning scene is visible
      // before it fills the viewport.
      const dr = daybreak.getBoundingClientRect();
      const q = dr.top / vh; // 1 = Daybreak's top is at the viewport bottom; 0 = it fills the viewport

      // Growth: a tiny point on the dark gate grows to full screen as Daybreak nears.
      let scale;
      if (q >= 1.5) scale = 0.04;
      else if (q > 1.0) scale = lerp(0.04, MAX_SCALE, easeInOut((1.5 - q) / 0.5));
      else scale = MAX_SCALE;

      // Opacity: fade in while growing -> hold briefly -> clear while Daybreak
      // enters. The wash stays tinted/transparent enough to read as a designed
      // transition, not a broken white page.
      let op;
      if (q >= 1.5) op = 0;
      else if (q > 1.08) op = easeInOut((1.5 - q) / 0.42) * MAX_OPACITY;
      else if (q > 0.34) op = MAX_OPACITY;
      else if (q > 0) op = easeInOut(q / 0.34) * MAX_OPACITY;
      else op = 0;
      op = clamp(op, 0, 1);

      core.style.display = (op < 0.004) ? "none" : "block";
      core.style.opacity = op.toFixed(3);
      core.style.transform = "translate(-50%, -50%) scale(" + Math.max(0.04, scale).toFixed(3) + ")";

      // Soft outer halo follows the same hand-off and is gone once Daybreak fills the viewport.
      const halo = Math.sin(clamp((1.5 - q) / 1.5, 0, 1) * Math.PI);
      flare.style.display = halo < 0.012 ? "none" : "block";
      flare.style.opacity = (halo * 0.2).toFixed(3);
      flare.style.transform = "scale(" + (1 + halo * 0.35).toFixed(3) + ")";
    }

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
  }

  // Decor motifs: read the seed list, inject transparent images into target
  // sections, give each idle motion + (optional) scroll parallax. Non-interactive.
  function bindDecor(root) {
    const store = root.querySelector("#ht-decor-store");
    if (!store) return;
    const seeds = Array.from(store.querySelectorAll(".ht-decor-seed"));
    if (!seeds.length) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const motionClass = { float: "ht-m-float", sway: "ht-m-sway", spin: "ht-m-spin", pulse: "ht-m-pulse" };
    const items = [];

    seeds.forEach((seed) => {
      const d = seed.dataset;
      if (!d.src) return;
      const host = root.querySelector("." + (d.target || "hero"));
      if (!host) return;
      if (getComputedStyle(host).position === "static") host.style.position = "relative";
      if (getComputedStyle(host).overflow === "visible") host.style.overflow = "hidden";

      const flip = d.flip === "true" ? -1 : 1;
      const wrap = document.createElement("div");
      wrap.className = "ht-decor" + (d.hideMobile === "true" ? " ht-decor-hide-sm" : "");
      wrap.style.left = (d.x || 50) + "%";
      wrap.style.top = (d.y || 50) + "%";
      wrap.style.width = (d.size || 12) + "vw";
      wrap.style.opacity = ((parseFloat(d.opacity) || 85) / 100).toString();
      wrap.style.zIndex = d.layer === "front" ? "7" : "1";
      wrap.style.transform = "translate(-50%, -50%) scaleX(" + flip + ")";

      const img = document.createElement("img");
      img.className = "ht-decor-img" + (!reduce && motionClass[d.motion] ? " " + motionClass[d.motion] : "");
      img.src = d.src; img.alt = ""; img.loading = "lazy"; img.decoding = "async";
      const dur = 6 + Math.random() * 6;
      img.style.setProperty("--dur", dur.toFixed(1) + "s");
      img.style.animationDelay = (-Math.random() * dur).toFixed(1) + "s";

      wrap.appendChild(img);
      host.appendChild(wrap);
      items.push({ wrap, flip, depth: (parseFloat(d.depth) || 0) / 100, pageY: 0 });
    });
    if (!items.length) return;

    function measure() {
      const sy = window.scrollY;
      items.forEach((it) => {
        it.wrap.style.transform = "translate(-50%, -50%) scaleX(" + it.flip + ")";
        const r = it.wrap.getBoundingClientRect();
        it.pageY = r.top + sy + r.height / 2;
      });
    }
    function frame() {
      const sy = window.scrollY, vh = window.innerHeight;
      items.forEach((it) => {
        const ty = it.depth ? (((sy + vh / 2) - it.pageY) * it.depth) : 0;
        it.wrap.style.transform = "translate(-50%, -50%) translateY(" + ty.toFixed(1) + "px) scaleX(" + it.flip + ")";
      });
    }
    measure(); frame();
    window.addEventListener("resize", () => { measure(); frame(); });
    if (!reduce) window.addEventListener("scroll", frame, { passive: true });
  }
})();
